// ---------------------------------------------------------------------------
// submitOrders — a player locks in their round
// ---------------------------------------------------------------------------
//
// What useGameState's submit did inside React, as a pure function:
//
//   - this round's brood builds are declared (read off the order board BEFORE
//     maturation, which clears the round's pending records)
//   - every corridor matures (matureCorridor: greens and moved-in drones
//     become veterans in place, round flags reset)
//   - each rocket bought is stationed on the Grid (addRocket), and paid for
//   - each drone aimed this round is declared, and paid for
//
// Spend is deducted here; income is not — yield is credited at resolution
// (resolveRound), off the Grid the round leaves behind.
//
// Every plan is checked first (validateOrders): an illegal one throws an
// IllegalOrdersError listing what is wrong, and nothing is submitted.

import { BATTLEFIELD_COUNT } from './gameConfig.js'
import { rules, withRules } from './config.js'
import { matureCorridor, buildRoundActionsLedger } from './gameLogic.js'
import { addRocket } from './subplotRockets.js'
import { validateOrders } from './validateOrders.js'
import { migrateBroods } from './subplotGrid.js'

export class IllegalOrdersError extends Error {
    constructor(errors) {
        super(`Illegal orders: ${errors.join(' ')}`)
        this.name = 'IllegalOrdersError'
        this.errors = errors
    }
}

const cloneJson = (v) => (v == null ? v : JSON.parse(JSON.stringify(v)))
// Hives saved before rules v8 still hold their Broods among the structures.
const parseHf = (raw) => (raw == null || raw === '' ? null : migrateBroods(typeof raw === 'string' ? JSON.parse(raw) : cloneJson(raw)))
const noTargets = () => Array(BATTLEFIELD_COUNT).fill(null)

// submitOrders(player, orders) → player
//
//   player  { board, homefront, yield, structuresLost, ... } — the player as
//           the last resolution left them (board = the start-of-round board;
//           it is replaced by the matured orders.board)
//   orders  { board, targetPlots, targetSubplots, dropZones,
//             rockets: [subS, ...], drones: [{ launchS, targetS }, ...] }
//           `board` is the planning board with this round's moves, deploys,
//           attacks and brood builds applied (gameLogic order functions)
//
// Returns the player ready for resolveRound, plus `spent` (this round's
// rocket + drone cost), `rocketsStationed` (the subplots that took one) and
// `actions` (the round's ledger, for the opponent's replay: maturing the
// board erases what it reads).
// `config` (optional) overrides DEFAULT_RULES numbers (rules/config.js).
export function submitOrders(player, orders, { config = null } = {}) {
    return withRules(config, () => {
        const v = validateOrders(player, orders)
        if (!v.ok) throw new IllegalOrdersError(v.errors)
        return submitOrdersWith(player, orders)
    })
}

function submitOrdersWith(player, orders) {
    const board = cloneJson(orders.board)
    // Declared broods, captured pre-maturation (several per corridor are
    // legal; the order matches the ledger's build pairs).
    const baseBuilds = board.flatMap((bf, col) => (bf.pendingBaseBuilds ?? []).map(p => ({ col, centerS: p.centerS })))
    const droneStrikes = (orders.drones ?? []).filter(d => d?.targetS != null).map(d => ({ ...d }))
    const actions = buildRoundActionsLedger(board)

    let homefront = parseHf(player.homefront)
    let yieldVal = player.yield ?? 0
    let spent = 0
    const rocketsStationed = []
    // The app charges only a player whose Grid it could read (a war always
    // has one); a Grid-less player pays nothing, as before.
    if (homefront) {
        let rocketCost = 0
        for (const subS of orders.rockets ?? []) {
            const structureId = homefront.subplots?.[subS]?.structureId
            if (structureId == null) continue
            const res = addRocket(homefront, structureId, subS)
            if (res.success) { homefront = res.homefront; rocketCost += rules().rocketCost; rocketsStationed.push(subS) }
        }
        spent = rocketCost + droneStrikes.length * rules().droneCost
        yieldVal -= spent
    }

    return {
        ...cloneJson(player),
        board: board.map(matureCorridor),
        homefront,
        yield: yieldVal,
        targetPlots: cloneJson(orders.targetPlots) ?? noTargets(),
        targetSubplots: cloneJson(orders.targetSubplots) ?? noTargets(),
        dropZones: cloneJson(orders.dropZones) ?? [],
        baseBuilds,
        droneStrikes,
        spent,
        rocketsStationed,
        actions,
    }
}
